import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import basketballLogo from "./images/basketball.png";
import dashboardShot from "./images/dashboard_screenshot.png";
import liveGameShot from "./images/live_game_screenshot.png";
import draftBoardShot from "./images/draft_screenshot.png";
import useAuthDarkTheme from "./useAuthDarkTheme";
import "./variables.css";
import "./Landing.css";

const FEATURES = [
  {
    eyebrow: "Build",
    title: "Draft & Sign Your Roster",
    copy:
      "Run a full draft board, scout prospects, and work the free-agent market to shape the team exactly the way you want it.",
    icon: "🏗️",
  },
  {
    eyebrow: "Compete",
    title: "Play Games in Real Time",
    copy:
      "Coach quarter-by-quarter through a live game engine with lineups, injuries, and momentum.",
    icon: "🏀",
  },
  {
    eyebrow: "Develop",
    title: "Grow Every Player",
    copy:
      "Practice plans, progression results, and offseason training turn a raw rookie into a franchise cornerstone.",
    icon: "📈",
  },
  {
    eyebrow: "Manage",
    title: "Run the Front Office",
    copy:
      "Negotiate trades, balance the cap, track standings, and chase a title across multiple saved franchises.",
    icon: "🧠",
  },
];

const SCREENSHOTS = [
  {
    label: "Dashboard",
    caption: "Your team at a glance — record, schedule, playoff picture.",
    image: dashboardShot,
    alt: "Courtside dashboard showing team record, schedule, and standings",
  },
  {
    label: "Live Game",
    caption: "Call plays, ride the hot hand, and manage the clock in real time.",
    image: liveGameShot,
    alt: "Courtside live game screen with in-game action and momentum",
  },
  {
    label: "Draft Board",
    caption: "Scout prospects and build the future of your franchise.",
    image: draftBoardShot,
    alt: "Courtside draft board with prospects and selection order",
  },
];

const TEAM = [
  { name: "Sean Nary", linkedin: "https://www.linkedin.com/in/sean-nary/" },
  { name: "Jack Hendley", linkedin: "https://www.linkedin.com/in/jack-hendley-a229aa401/" },
  { name: "Joshua Arega", linkedin: "https://www.linkedin.com/in/joshua-arega-8a9440294/" },
  { name: "Victoria Vanlue", linkedin: "https://www.linkedin.com/in/victoriavanlue/" }
];

const GITHUB_URL = "https://github.com/Seanopher/SingularitySpurs";
const LIVE_DEMO_URL = "https://courtside-game.onrender.com/";
const YOUTUBE_DEMO_URL = "https://youtu.be/PhEZ7rMQ0go";

const Landing = () => {
  useAuthDarkTheme();
  const navigate = useNavigate();
  const revealRefs = useRef([]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      revealRefs.current.forEach((el) => el && el.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const registerReveal = (el, index) => {
    revealRefs.current[index] = el;
  };

  return (
    <div className="landing-page">
      {/* ── Top nav ─────────────────────────────────────────────────────── */}
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <a href="#top" className="landing-brand" aria-label="Courtside home">
            <img src={basketballLogo} alt="" className="landing-brand-logo" />
            <span className="landing-brand-name">Courtside</span>
          </a>
          <nav className="landing-nav-links" aria-label="Primary">
            <a href="#features" className="landing-nav-link">Features</a>
            <a href="#demo" className="landing-nav-link">Demo</a>
            <a href="#about" className="landing-nav-link">Team</a>
            <button
              type="button"
              className="cta-btn cta-secondary landing-nav-cta"
              onClick={() => navigate("/login")}
            >
              Log In
            </button>
            <button
              type="button"
              className="cta-btn cta-primary landing-nav-cta"
              onClick={() => navigate("/register")}
            >
              Sign Up
            </button>
          </nav>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="landing-hero" id="top">
        <div className="landing-hero-bg" aria-hidden="true" />
        <div className="landing-hero-inner">
          <span className="landing-eyebrow">Basketball · Management · Simulation</span>
          <h1 className="landing-hero-title">
            Run the franchise.
            <br />
            <span className="landing-hero-accent">Own the Courtside.</span>
          </h1>
          <p className="landing-hero-sub">
            Courtside is a full-season basketball management sim. <br />
            Draft, trade, coach, and develop your way to a title. <br />
            Built for basketball fans who want more than just box scores.
          </p>
          <div className="landing-hero-ctas">
            <button
              type="button"
              className="cta-btn cta-primary landing-cta-lg"
              onClick={() => navigate("/register")}
            >
              Start Your Franchise
            </button>
            <a
              href="#demo"
              className="cta-btn cta-secondary landing-cta-lg"
            >
              Watch the Demo
            </a>
          </div>
          <div className="landing-hero-meta">
            <span className="landing-hero-meta-item">
              <span className="landing-dot landing-dot--live" /> Live demo available
            </span>
            <span className="landing-hero-meta-sep">·</span>
            <span className="landing-hero-meta-item">Free to play</span>
          </div>
        </div>
      </section>

      {/* ── Demo video ──────────────────────────────────────────────────── */}
      <section className="landing-section" id="demo">
        <div className="landing-section-inner">
          <div className="landing-section-head">
            <span className="dash-card-eyebrow">Final Demo</span>
            <h2 className="landing-section-title">See a full season in motion</h2>
            <p className="landing-section-sub">
              A quick tour of the dashboard, live game engine, and front-office tools.
            </p>
          </div>

          <div
            className="dash-card landing-video-card landing-reveal"
            ref={(el) => registerReveal(el, 0)}
          >
            <div className="landing-video-frame">
              <iframe
                className="landing-video-el"
                src="https://www.youtube.com/embed/PhEZ7rMQ0go"
                title="Courtside Final Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="landing-section" id="features">
        <div className="landing-section-inner">
          <div className="landing-section-head">
            <span className="dash-card-eyebrow">Why Courtside</span>
            <h2 className="landing-section-title">Every tool a GM actually needs</h2>
            <p className="landing-section-sub">
              From draft night to the championship, Courtside gives you the tools to build, coach, and grow a franchise worth following.
            </p>
          </div>

          <div className="landing-feature-grid">
            {FEATURES.map((feature, i) => (
              <article
                key={feature.title}
                className="dash-card landing-feature-card landing-reveal"
                ref={(el) => registerReveal(el, i + 1)}
              >
                <div className="landing-feature-icon" aria-hidden="true">
                  {feature.icon}
                </div>
                <span className="dash-card-eyebrow">{feature.eyebrow}</span>
                <h3 className="landing-feature-title">{feature.title}</h3>
                <p className="landing-feature-copy">{feature.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Screenshots ─────────────────────────────────────────────────── */}
      <section className="landing-section landing-section--muted" id="screens">
        <div className="landing-section-inner">
          <div className="landing-section-head">
            <span className="dash-card-eyebrow">Inside the app</span>
            <h2 className="landing-section-title">From tip-off to trade deadline</h2>
          </div>

          <div className="landing-shot-grid">
            {SCREENSHOTS.map((shot, i) => (
              <figure
                key={shot.label}
                className="dash-card landing-shot landing-reveal"
                ref={(el) => registerReveal(el, i + 1 + FEATURES.length)}
              >
                <div className="landing-shot-frame">
                  <img className="landing-shot-image" src={shot.image} alt={shot.alt} />
                  <div className="landing-shot-overlay" aria-hidden="true" />
                  <span className="landing-shot-badge">{shot.label}</span>
                </div>
                <figcaption className="landing-shot-caption">{shot.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ── About / Team ────────────────────────────────────────────────── */}
      <section className="landing-section" id="about">
        <div className="landing-section-inner">
          <div className="landing-section-head">
            <span className="dash-card-eyebrow">The Team</span>
            <h2 className="landing-section-title">Built by the SingularitySpurs</h2>
          </div>

          <div className="landing-team-grid">
            {TEAM.map((member, i) => (
              <article
                key={member.name}
                className="dash-card landing-team-card landing-reveal"
                ref={(el) =>
                  registerReveal(el, i + 1 + FEATURES.length + SCREENSHOTS.length)
                }
              >
                <div className="landing-team-avatar" aria-hidden="true">
                  {member.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                <h3 className="landing-team-name">{member.name}</h3>
                {member.linkedin ? (
                  <a
                    className="landing-team-link"
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn →
                  </a>
                ) : (
                  <span className="landing-team-link landing-team-link--muted">
                    LinkedIn coming soon
                  </span>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA band ────────────────────────────────────────────────────── */}
      <section className="landing-cta-band">
        <div className="landing-section-inner landing-cta-band-inner">
          <h2 className="landing-cta-band-title">Ready to take your seat courtside?</h2>
          <div className="landing-hero-ctas">
            <button
              type="button"
              className="cta-btn cta-primary landing-cta-lg"
              onClick={() => navigate("/register")}
            >
              Create Free Account
            </button>
            <button
              type="button"
              className="cta-btn cta-secondary landing-cta-lg"
              onClick={() => navigate("/login")}
            >
              I Already Have One
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-section-inner landing-footer-inner">
          <div className="landing-footer-brand">
            <img src={basketballLogo} alt="" className="landing-brand-logo" />
            <div>
              <p className="landing-footer-name">Courtside</p>
              <p className="landing-footer-tag">
                © {new Date().getFullYear()} SingularitySpurs. All rights reserved.
              </p>
            </div>
          </div>
          <nav className="landing-footer-links" aria-label="Footer">
            <a href="#features" className="landing-footer-link">Features</a>
            <a href="#demo" className="landing-footer-link">Demo</a>
            <a href="#about" className="landing-footer-link">Team</a>
            <a
              href={GITHUB_URL}
              className="landing-footer-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
